import {
  IAlertTypeItemData,
  IDocumentMediaPreviewData,
  IInfoType,
  IMessage,
  IOrganizationUser,
  ITemplate,
  TemplateKey,
} from "@home-health-notify/domain";
import CloseIcon from "@mui/icons-material/Close";
import { Collapse, IconButton, Tooltip } from "@mui/material";
import {
  getRouteType,
  IOrganizationContext,
  OrganizationContext,
  unSupportedIsMentionedUserHasPatient,
  unSupportedIsMentionedUserHasPhysician,
} from "hhn-commons";
import { useContext } from "react";
import { useNavigate } from "react-router";
import MessagePanelItem from "../message/messagePanelItem/MessagePanelItem";
import { ROUTES } from "../routes";
import { AlertMessageTypeListItemButton } from "./Alert.style";
import AlertListItemHeader from "./AlertListItemHeader";
import { AlertTypeDataProps } from "./AlertMessagesList";
import { DocumentMediaViewerContext } from "../context/DocumentMediaViewerContext";

interface AlertMessageTypeItemProps {
  data: AlertTypeDataProps;
  in?: boolean; // This will be passed by TransitionGroup
}

const AlertMessageTypeItem = ({
  data,
  in: inProp,
}: AlertMessageTypeItemProps) => {
  const { selectedOrgUser } =
    useContext<IOrganizationContext>(OrganizationContext);
  const navigate = useNavigate();

  const goToChat = (data: IAlertTypeItemData) => {
    if (!data.info) return;
    navigate(
      `../${ROUTES.CHATS}/${getRouteType(data)}/${data.info.id}/message`,
      {
        state: { focusMessageId: data.message?.getMessage().id },
      },
    );
  };

  const UnSupported = () => {
    throw new Error("Panic: message selection not allowed on alert messages");
  };

  const getTemplateByKey = (
    templateKey: TemplateKey,
  ): ITemplate | undefined => {
    return selectedOrgUser?.getTemplateByKey(
      templateKey,
      data.info as IInfoType,
    );
  };

  const { openDocumentMediaViewer } = useContext(
    DocumentMediaViewerContext,
  ) as {
    openDocumentMediaViewer: (
      previewMedia: IDocumentMediaPreviewData,
      previewMediaList: IDocumentMediaPreviewData[],
    ) => void;
  };

  const openMediaDialog = (message: IMessage) => {
    const previewMedia = message.getPreviewDataFromMessage();
    if (!previewMedia) return;
    const previewMediaList: IDocumentMediaPreviewData[] = [previewMedia];
    openDocumentMediaViewer(previewMedia, previewMediaList);
  };

  return (
    <Collapse in={inProp} timeout={300} enter={false}>
      <AlertMessageTypeListItemButton
        data-testid="alert-list-item"
        onClick={() => goToChat(data)}
        disableGutters
        disableRipple
        divider
        sx={{
          backgroundColor: data.isRead ? "rgba(0,0,0,0.06)" : "inherit",
          transition: "background-color 0.2s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", width: "99%" }}>
          <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            <AlertListItemHeader data={data} />
          </div>
          {data.isRead && data.onDismiss && (
            <Tooltip title="Dismiss">
              <IconButton
                size="small"
                edge="end"
                aria-label="dismiss"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setTimeout(() => {
                    data.onDismiss && data.onDismiss();
                  }, 300);
                }}
                sx={{ color: "grey.600" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {!!data.info && data.message && (
          <MessagePanelItem
            message={data.message}
            selectedOrgUser={selectedOrgUser as IOrganizationUser}
            messageBubbleFullWidth={true}
            hideAvatar
            hideContextMenu={true}
            chatInfoAccess={{
              chatInfo: data.info,
              isMentionedUserHasPatient: unSupportedIsMentionedUserHasPatient,
              isMentionedUserHasPhysician:
                unSupportedIsMentionedUserHasPhysician,
              getTemplateByKey,
            }}
            selectMessage={UnSupported}
            isReplyPreview={true}
            openMediaDialog={openMediaDialog}
          />
        )}
      </AlertMessageTypeListItemButton>
    </Collapse>
  );
};

export default AlertMessageTypeItem;
